import type Button from '../button/Button.js'
import type Calendar from '../calendar/Calendar.js'
import type { DatePredicate } from '../calendar/Calendar.js'
import type { DateSelectEvent } from '../calendar/DateSelectEvent.js'
import type Popout from '../popout/Popout.js'
import type { DateAdapter } from './date-adapter.js'

import { ContextProvider } from '@lit/context'
import * as calendarIcon from '@nordhealth/icons/lib/assets/interface-calendar.js'
import * as closeIcon from '@nordhealth/icons/lib/assets/interface-close-small.js'

import { html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ref } from 'lit/directives/ref.js'
import { isDownwardsSwipe, SwipeController } from '../common/controllers/SwipeController.js'
import { createDate, DaysOfWeek, parseISODate, printISODate } from '../common/dates.js'

import { cond } from '../common/directives/cond.js'
import { NordEvent } from '../common/events.js'
import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import { FormAssociatedMixin } from '../common/mixins/FormAssociatedMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'
import { ReadonlyMixin } from '../common/mixins/ReadonlyMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'
import componentStyle from '../common/styles/Component.css'
import formFieldStyle from '../common/styles/FormField.css'

import textFieldStyle from '../common/styles/TextField.css'
import { fieldContext } from '../field/field-context.js'
import Icon from '../icon/Icon.js'

import { LocalizeController } from '../localization/LocalizeController.js'
import { isoAdapter } from './date-adapter.js'
import style from './DatePicker.css'
import '../input/Input.js'
import '../button/Button.js'

import '../visually-hidden/VisuallyHidden.js'
import '../popout/Popout.js'
import '../stack/Stack.js'
import '../calendar/Calendar.js'

Icon.registerIcon(calendarIcon)
Icon.registerIcon(closeIcon)

const isDateDisabled = () => false
const isDateHighlighted = () => false

/**
 *
 * Date Picker allows user to enter a date either through text input,
 * or by choosing a date from the calendar. Please note that the date
 * must be passed in ISO-8601 format: YYYY-MM-DD.
 *
 * @status ready
 * @category form
 *
 * @fires {NordEvent} open - Dispatched when the popout is opened.
 * @fires {NordEvent} close - Dispatched when the popout is closed.
 * @fires {DateSelectEvent} nord-focus-date - Dispatched when the calendar's focused date changes.
 *
 * @localization modalHeading - Heading for the date picker's modal.
 * @localization closeLabel - Accessible label for the close button.
 * @localization buttonLabel - Accessible label for the toggle button that opens the date picker modal.
 * @localization selectedDateMessage - Describes the selected date. This message is appended to the toggle button label when a date is selected.
 * @localization dateOutOfMinBound - Error message shown when the entered date is below the minimum allowed date.
 * @localization dateOutOfMaxBound - Error message shown when the entered date is above the maximum allowed date.
 *
 * @cssprop [--n-label-color=var(--n-color-text)] - Controls the text color of the label, using our [color tokens](/tokens/#color).
 */
@customElement('nord-date-picker')
export default class DatePicker extends SizeMixin(
  FormAssociatedMixin(ReadonlyMixin(InputMixin(FocusableMixin(LitElement)))),
) {
  static styles = [componentStyle, formFieldStyle, textFieldStyle, style]

  // Barrier: the composed inner <nord-input> would otherwise consume the same
  // Field context and double up the label/description. Provide `undefined` to the
  // subtree so the inner input is driven entirely by this component's props.
  private fieldBarrier = new ContextProvider(this, { context: fieldContext, initialValue: undefined })

  @query(`.n-date-picker-toggle`, true) private toggleButton!: Button
  @query(`.n-date-picker-close-button`, true) private closeButton!: HTMLButtonElement
  @query(`nord-calendar`, true) private calendar!: Calendar
  @query(`[role="dialog"]`, true) private popout!: Popout

  private swipe = new SwipeController(this, {
    target: () => this.popout,
    matchesGesture: isDownwardsSwipe,
    onSwipeEnd: () => this.hide(),
  })

  private localize = new LocalizeController<'nord-date-picker'>(this, {
    onLangChange: () => this.createDateFormatters(),
  })

  /**
   * Whilst dateAdapter is used for handling the formatting/parsing dates in the input,
   * these are used to format dates exclusively for the benefit of screen readers.
   *
   * We prefer DateTimeFormat over date.toLocaleDateString, as the former has
   * better performance when formatting large number of dates. See:
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#Performance
   */
  private dateFormatLong!: Intl.DateTimeFormat

  /**
   * Date value. Must be in IS0-8601 format: YYYY-MM-DD.
   */
  @property() value: string = ''

  /**
   * Internal validation error message. Set automatically when min/max validation fails.
   * This is a read-only property that's managed by the component.
   */
  @property({ attribute: false }) internalError?: string

  get valueAsDate(): Date | undefined {
    return parseISODate(this.value)
  }

  /**
   * Get/set the value of the picker as a Date object.
   */
  set valueAsDate(date: Date | undefined) {
    this.value = date ? printISODate(date) : ''
  }

  get valueAsNumber(): number {
    return this.valueAsDate?.getTime() ?? Number.NaN
  }

  /**
   * Get/set the value of the picker as the number of milliseconds elapsed since the UNIX epoch.
   */
  set valueAsNumber(date: number) {
    this.value = date ? printISODate(new Date(date)) : ''
  }

  /**
   * Get the raw value of the input field without date formatting. This is a read-only property.
   */
  @property({ attribute: false }) rawValue?: string

  /**
   * Controls whether date picker dialog is open or not.
   */
  @property({ type: Boolean, reflect: true }) open = false

  /**
   * Minimum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the max property.
   */
  @property({ reflect: true }) min?: string

  /**
   * Maximum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the min property.
   */
  @property({ reflect: true }) max?: string

  /**
   * The date that is considered today. Must be in IS0-8601 format: YYYY-MM-DD.
   * If not set, the current local date of the user is used.
   */
  @property({ reflect: true }) today?: string

  /**
   * Which day is considered first day of the week? `0` for Sunday, `1` for Monday, etc.
   * Default is Monday.
   */
  @property({ reflect: true, attribute: 'first-day-of-week', type: Number }) firstDayOfWeek: DaysOfWeek
    = DaysOfWeek.Monday

  /**
   * Displays the ISO-8601 week number of each visible week in a leading column
   * of the calendar. Forwarded to `nord-calendar`.
   */
  @property({ reflect: true, type: Boolean, attribute: 'week-numbers' }) weekNumbers = false

  /**
   * Date adapter, for custom parsing/formatting.
   * Must be object with a `parse` function which accepts a `string` and returns a `Date`,
   * and a `format` function which accepts a `Date` and returns a `string`.
   * Default is IS0-8601 parsing and formatting.
   */
  @property({ attribute: false }) dateAdapter: DateAdapter = isoAdapter

  /**
   * Controls which days are disabled and therefore disallowed.
   * For example, this can be used to disallow selection of weekends.
   */
  @property({ attribute: false }) isDateDisabled: DatePredicate = isDateDisabled

  /**
   * Controls which days are highlighted with a small indicator.
   * Returning `false` will not show an indicator.
   * Returning `true` will show the indicator, but without an accessible label. Therefore
   * Returning a string will show the indicator, and use the string as accessible label.
   * It is recommended to return a string rather than `true` whenever possible.
   */
  @property({ attribute: false }) isDateHighlighted: (date: Date) => string | boolean = isDateHighlighted

  /**
   * Controls whether the date picker expands to fill the width of its container.
   */
  @property({ reflect: true, type: Boolean }) expand = false

  // The control's own invalid state for the field context: a built-in/internal
  // error. (When inside a Field, the Field's `invalid` is ORed in by the mixin.)
  protected override get ownInvalid() {
    return Boolean(this.error || this.internalError) || super.ownInvalid
  }

  firstUpdated() {
    // Make sure the date picker has a raw value when the value is initially set
    this.rawValue = (<HTMLInputElement | null> this.focusableRef.value)?.value
  }

  render() {
    const { valueAsDate } = this
    const formattedDate = valueAsDate ? this.dateAdapter.format(valueAsDate) : ''

    return html`
      <nord-input
        class="n-date-picker-input"
        value=${formattedDate}
        label=${this.insideField ? nothing : ifDefined(this.label)}
        hint=${this.insideField ? nothing : ifDefined(this.hint)}
        error=${this.insideField ? nothing : cond(this.error || this.internalError, this.error || this.internalError)}
        placeholder=${ifDefined(this.placeholder)}
        id=${this.inputId}
        size=${this.size}
        ?expand=${this.expand}
        ?disabled=${this.effectiveDisabled}
        ?required=${this.required}
        ?readonly=${this.readonly}
        ?hint-below=${this.hintBelow}
        ?hide-label=${this.insideField || this.hideLabel}
        ?hide-required=${this.hideRequired}
        disallow-pattern="[^0-9./-]"
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
        @input=${this.handleInputChange}
        ${ref(this.focusableRef)}
        aria-label=${ifDefined(this.controlAriaLabel)}
        aria-invalid=${ifDefined(this.controlAriaInvalid)}
        aria-describedby=${ifDefined(this.controlAriaDescribedby)}
      >
        ${this.insideField || this.hintSlot.isEmpty ? nothing : html`<slot name="hint" slot="hint"></slot>`}
        ${this.insideField || this.labelSlot.isEmpty ? nothing : html`<slot name="label" slot="label"></slot>`}
        <nord-button
          size=${this.size}
          ?disabled=${this.disabled || this.readonly}
          slot="end"
          class="n-date-picker-toggle"
          aria-controls="popout"
          type="button"
        >
          <nord-icon name="interface-calendar"></nord-icon>
          <nord-visually-hidden>
            ${this.localize.term('buttonLabel')}
            ${valueAsDate
              ? html`
                  <span>
                    , ${this.localize.term('selectedDateMessage')} ${this.dateFormatLong.format(valueAsDate)}
                  </span>
                `
              : nothing}
          </nord-visually-hidden>
        </nord-button>
      </nord-input>
      <nord-popout
        id="popout"
        anchor=${this.inputId}
        align="end"
        position="block-end"
        role="dialog"
        aria-modal="true"
        ?open=${this.open}
        @open=${this.handleOpen}
        @close=${this.handleClose}
        aria-labelledby="header"
      >
        <div aria-hidden="true" tabindex="0" @focus=${this.focusLast}></div>

        <nord-stack class="n-date-picker-header" direction="horizontal" justify-content="space-between">
          <div class="n-date-picker-heading" id="header">${this.localize.term('modalHeading')}</div>
          <nord-button
            class="n-date-picker-close-button"
            type="button"
            size="s"
            variant="plain"
            @click=${this.handleClose}
          >
            <nord-visually-hidden>${this.localize.term('closeLabel')}</nord-visually-hidden>
            <nord-icon name="interface-close-small"></nord-icon>
          </nord-button>
        </nord-stack>

        <nord-calendar
          class="n-date-picker-calendar"
          expand
          value=${this.value}
          min=${ifDefined(this.min)}
          max=${ifDefined(this.max)}
          today=${ifDefined(this.today)}
          ?week-numbers=${this.weekNumbers}
          .firstDayOfWeek=${this.firstDayOfWeek}
          .isDateDisabled=${this.isDateDisabled}
          .isDateHighlighted=${this.isDateHighlighted}
          @change=${this.handleDaySelect}
        ></nord-calendar>

        <div aria-hidden="true" tabindex="0" @focus=${this.focusFirst}></div>
      </nord-popout>
    `
  }

  private createDateFormatters() {
    this.dateFormatLong = new Intl.DateTimeFormat(this.localize.resolvedLang, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  private focusFirst() {
    this.closeButton.focus()
  }

  private focusLast() {
    this.calendar.focus({ target: 'day' })
  }

  private handleDaySelect = (e: DateSelectEvent) => {
    e.stopPropagation()
    this.setValue(e.date)
    this.hide()
  }

  private handleOpen() {
    this.open = true
    this.calendar.focus({ target: 'month' })
  }

  private handleClose() {
    this.open = false
  }

  private handleBlur = (event: Event) => {
    event.stopPropagation()

    // Validate on blur and set internal error
    this.internalError = this.validateDateBounds(this.valueAsDate)

    this.dispatchEvent(new NordEvent('blur'))
  }

  private handleFocus = (event: Event) => {
    event.stopPropagation()
    this.dispatchEvent(new NordEvent('focus'))
  }

  private handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement

    const parsed = this.dateAdapter.parse(target.value, createDate)
    this.rawValue = (<HTMLInputElement | null> this.focusableRef.value)?.value

    if (parsed || target.value === '') {
      this.setValue(parsed)
    }
  }

  private setValue(date?: Date) {
    this.value = date ? printISODate(date) : ''

    const { valueAsDate } = this
    const formattedDate = valueAsDate ? this.dateAdapter.format(valueAsDate) : ''
    this.rawValue = formattedDate

    this.dispatchEvent(new NordEvent('change'))
  }

  private validateDateBounds(date: Date | undefined): string | undefined {
    if (!date)
      return undefined

    const minDate = parseISODate(this.min || '')
    const maxDate = parseISODate(this.max || '')

    if (minDate && date < minDate) {
      return this.localize.term('dateOutOfMinBound', this.min!)
    }
    if (maxDate && date > maxDate) {
      return this.localize.term('dateOutOfMaxBound', this.max!)
    }
  }

  /**
   * Hide the date picker programmatically.
   * @param moveFocusToButton A boolean option to move the focus to the original button that opens the popout.
   */
  hide(moveFocusToButton?: boolean) {
    this.popout.hide(moveFocusToButton)
  }

  /**
   * Show the date picker programmatically.
   */
  show() {
    this.popout.show()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-date-picker': DatePicker
  }
}
